import { useQuery } from '@apollo/client';
import { DeviceHub } from '@mui/icons-material';
import { Box, Button, TextField } from '@mui/material';
import { useFormik } from 'formik';
import { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { boolean, number, object, string } from 'yup';
import { useDeleteDeviceGroup, useUpdateDeviceGroup } from '~/api/device-groups';
import { RouterBreadcrumbs } from '~/components/RouterBreadcrumbs';
import { ScrollableRouterTabs } from '~/components/RouterTabs';
import type { ShowAutocomplete__Show as Show } from '~/components/ShowAutocomplete';
import { ShowAutocomplete } from '~/components/ShowAutocomplete';
import { Tooltip } from '~/components/Tooltip';
import { DeleteButton, SaveButton } from '~/components/button';
import { ConfirmDialog } from '~/components/dialogs/confirmation';
import { DetailSwitch, DetailTextArea, DetailTextField } from '~/components/forms/details';
import { LoadingPane } from '~/components/loading-pane';
import { PageContainer } from '~/components/page-layout';
import {
  SettingDescription,
  SettingFieldRequired,
  SettingInputWrapper,
  SettingLabelWrapper,
  SettingName,
  SettingsBody,
  SettingsData,
  SettingsHead,
} from '~/components/settings';
import { Timestamps } from '~/components/timestamps';
import { Toolbar } from '~/components/toolbar';
import { useAppContext } from '~/contexts';
import { useConfirmDialog } from '~/hooks/dialogs';
import { useLink } from '~/hooks/link';
import { toDataUri } from '~/lib/file';
import { i18n } from '~/lib/i18n';
import { NetworkDeviceGroupSettingsDocument } from '../queries/settings.generated';
import { useTabs } from './lib/tabs';

const validationSchema = object({
  name: string().required(i18n.t('errors.validation.required')).label(i18n.t('common.labels.name')),
  description: string().optional().label(i18n.t('common.labels.description')),
  defaultShowId: number()
    .nullable()
    .label(i18n.t('pages.device_groups.settings.validation.default_show_label')),
  enableReporting: boolean()
    .optional()
    .label(i18n.t('pages.device_groups.settings.validation.enable_reporting_label')),
  thumbnailUri: string().optional().label(i18n.t('common.labels.thumbnail')),
});

export const DeviceGroupSettings = () => {
  const { t } = useTranslation();
  const { currentNetwork, currentUser } = useAppContext();

  const tabs = useTabs();

  const navigate = useNavigate();

  const link = useLink();

  const params = useParams<{ deviceGroupId: string }>();

  const deviceGroupId = Number(params.deviceGroupId);

  const { data, loading, error } = useQuery(NetworkDeviceGroupSettingsDocument, {
    variables: { networkId: currentNetwork.id, deviceGroupId },
  });

  const [deleteDeviceGroup] = useDeleteDeviceGroup();
  const [updateDeviceGroup] = useUpdateDeviceGroup();

  const [confirmDelete, confirmDeleteProps] = useConfirmDialog();

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: data?.network?.deviceGroup.name ?? '',
      description: data?.network?.deviceGroup.description ?? '',
      defaultShowId: data?.network?.deviceGroup.defaultShow?.id,
      enableReporting: data?.network?.deviceGroup.enableReporting ?? true,
      // Only a new image is sent; the current one is previewed from the query below.
      thumbnailUri: '',
    },
    onSubmit: async (values) => {
      const newValues = validationSchema.cast(values);
      await updateDeviceGroup({
        variables: {
          patch: {
            ...newValues,
            defaultShowId: newValues.defaultShowId === -1 ? null : newValues.defaultShowId,
            thumbnailUri: newValues.thumbnailUri?.trim() || undefined,
          },
          id: deviceGroupId,
        },
      });
    },
    validateOnBlur: true,
    validationSchema,
  });

  const { getInputProps, open: selectFile } = useDropzone({
    accept: 'image/*',
    multiple: false,
    onDrop: async (accepted, _rejected, _event) => {
      if (accepted.length !== 1) return;
      const thumbnailUri = await toDataUri(accepted[0]);
      await formik.setFieldValue('thumbnailUri', thumbnailUri, true);
    },
  });

  const handleShowChange = useCallback(
    async (newValue: Show | null) => {
      await formik.setFieldValue('defaultShowId', newValue?.id ?? -1);
    },
    [formik],
  );

  const handleDelete = useCallback(async () => {
    if (!(await confirmDelete())) return;
    await deleteDeviceGroup({
      variables: { input: { deviceGroupIds: [deviceGroupId] } },
    });
    navigate(link('/device-groups'), { replace: true });
  }, [confirmDelete, deleteDeviceGroup, deviceGroupId, link, navigate]);

  const thumbnailSrc =
    formik.values.thumbnailUri.trim() || data?.network?.deviceGroup.thumbnailFile?.uri;

  if ((!loading && !data) || error) return <Navigate replace to="/not-found" />;

  return (
    <LoadingPane in={loading && !data} size={80} thickness={4}>
      <>
        <Toolbar
          actions={
            <Box sx={{ display: 'flex', gap: 2 }}>
              <SaveButton
                disabled={formik.isSubmitting || !formik.isValid || !formik.dirty}
                type="submit"
                form="update-device-group"
              >
                {t('common.actions.save')}
              </SaveButton>
              <Tooltip
                title={data?.network?.deviceGroup.canDestroy.reasons?.fullMessages.join(', ')}
              >
                <span>
                  <DeleteButton
                    disabled={!data?.network?.deviceGroup.canDestroy.value}
                    onClick={handleDelete}
                  >
                    {t('common.actions.delete')}
                  </DeleteButton>
                </span>
              </Tooltip>
            </Box>
          }
          breadcrumbsLabel={<RouterBreadcrumbs />}
          titleIcon={<DeviceHub />}
          titleText={data?.network?.deviceGroup.name}
        />

        <ScrollableRouterTabs current={t('common.labels.details')} tabs={tabs} />

        <PageContainer>
          <Box
            component="form"
            id="update-device-group"
            onSubmit={formik.handleSubmit}
            sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
          >
            <div>
              <SettingsHead>{t('common.labels.general')}</SettingsHead>
              <SettingsBody>
                <SettingsData>
                  <SettingLabelWrapper>
                    <SettingName>
                      {t('common.labels.name')}
                      <SettingFieldRequired>
                        {t('pages.device_groups.settings.fields.name.required')}
                      </SettingFieldRequired>
                    </SettingName>
                    <SettingDescription>
                      {t('pages.device_groups.settings.fields.name.description')}
                    </SettingDescription>
                  </SettingLabelWrapper>
                  <SettingInputWrapper>
                    <DetailTextField
                      autoFocus
                      disabled={formik.isSubmitting}
                      error={formik.touched.name && !!formik.errors.name}
                      fullWidth
                      helperText={(formik.touched.name && formik.errors.name) || ' '}
                      name="name"
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                      value={formik.values.name}
                    />
                  </SettingInputWrapper>
                </SettingsData>
                <SettingsData>
                  <SettingLabelWrapper>
                    <SettingName>{t('common.labels.description')}</SettingName>
                    <SettingDescription>
                      {t('pages.device_groups.settings.fields.description.description')}
                    </SettingDescription>
                  </SettingLabelWrapper>
                  <SettingInputWrapper>
                    <DetailTextArea
                      multiline
                      minRows={2}
                      maxRows={2}
                      disabled={formik.isSubmitting}
                      error={formik.touched.description && !!formik.errors.description}
                      fullWidth
                      helperText={(formik.touched.description && formik.errors.description) || ' '}
                      name="description"
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                      value={formik.values.description}
                    />
                  </SettingInputWrapper>
                </SettingsData>
                <SettingsData>
                  <SettingLabelWrapper>
                    <SettingName>{t('common.labels.thumbnail')}</SettingName>
                    <SettingDescription>
                      {t('pages.device_groups.settings.fields.thumbnail.description')}
                    </SettingDescription>
                  </SettingLabelWrapper>
                  <SettingInputWrapper>
                    <Box display="flex" flexDirection="row" gap={2}>
                      {thumbnailSrc && (
                        <Box
                          alignItems="center"
                          alignSelf="flex-start"
                          display="flex"
                          flexShrink={0}
                          height="8rem"
                          justifyContent="center"
                          sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}
                          width="8rem"
                        >
                          <img
                            alt={t('common.labels.current_thumbnail')}
                            src={thumbnailSrc}
                            style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }}
                          />
                        </Box>
                      )}
                      <Box display="flex" flexDirection="column" flexGrow={1} gap={2}>
                        <input {...getInputProps()} />
                        <div>
                          <Button onClick={selectFile} variant="outlined">
                            {t('pages.device_groups.settings.fields.thumbnail.upload_button')}
                          </Button>
                        </div>
                        <Box display="flex" flexDirection="column">
                          <span>
                            {t('pages.device_groups.settings.fields.thumbnail.url_prompt')}
                          </span>
                          <TextField
                            fullWidth
                            inputProps={{
                              'aria-label': t(
                                'pages.device_groups.settings.fields.thumbnail.url_prompt',
                              ),
                            }}
                            name="thumbnailUri"
                            onBlur={formik.handleBlur}
                            onChange={formik.handleChange}
                            size="small"
                            value={
                              formik.values.thumbnailUri.startsWith('http')
                                ? formik.values.thumbnailUri
                                : ''
                            }
                          />
                        </Box>
                      </Box>
                    </Box>
                  </SettingInputWrapper>
                </SettingsData>
                <SettingsData>
                  <SettingLabelWrapper>
                    <SettingName>
                      {t('pages.device_groups.settings.fields.default_show.label')}
                    </SettingName>
                    <SettingDescription>
                      {t('pages.device_groups.settings.fields.default_show.description')}
                    </SettingDescription>
                  </SettingLabelWrapper>
                  <SettingInputWrapper>
                    <ShowAutocomplete
                      onChange={handleShowChange}
                      value={data?.network?.deviceGroup.defaultShow ?? null}
                      sx={{
                        display: 'inline-flex', // hackeration so label is centered since it's set to baseline
                      }}
                    />
                  </SettingInputWrapper>
                </SettingsData>
                <SettingsData className="last">
                  <SettingLabelWrapper>
                    <SettingName>
                      {t('pages.device_groups.settings.fields.enable_reporting.label')}
                    </SettingName>
                    <SettingDescription>
                      {t('pages.device_groups.settings.fields.enable_reporting.description')}
                    </SettingDescription>
                  </SettingLabelWrapper>
                  <SettingInputWrapper>
                    <DetailSwitch
                      checked={formik.values.enableReporting}
                      color="primary"
                      disabled={formik.isSubmitting}
                      name="enableReporting"
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                    />
                  </SettingInputWrapper>
                </SettingsData>
              </SettingsBody>
            </div>

            {currentUser.admin && (
              <Timestamps
                createdAt={data?.network?.deviceGroup.createdAt ?? ''}
                updatedAt={data?.network?.deviceGroup.updatedAt ?? ''}
              />
            )}
          </Box>
        </PageContainer>
        <ConfirmDialog
          {...confirmDeleteProps}
          confirm={t('common.dialogs.permanently_delete')}
          deleteConfirm
          prompt={
            <span>
              {t('pages.device_groups.settings.delete_dialog.prompt')}{' '}
              <b>{t('common.warnings.cannot_be_undone')}</b>
            </span>
          }
          title={t('pages.device_groups.settings.delete_dialog.title')}
        />
      </>
    </LoadingPane>
  );
};
